// @ts-nocheck
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpcClient } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Plus, Pencil, Trash2, MapPin, Search } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SharedCombobox } from '@/components/shared/SharedCombobox';
import { toast } from 'sonner';

export function WarehouseLocationTab() {
  const queryClient = useQueryClient();
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<string>('ALL');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingLocation, setEditingLocation] = useState<any>(null);

  // Form State
  const [formWarehouseId, setFormWarehouseId] = useState<string>('');
  const [formCode, setFormCode] = useState<string>('');
  const [formName, setFormName] = useState<string>('');
  const [formAisle, setFormAisle] = useState<string>('');
  const [formShelf, setFormShelf] = useState<string>('');
  const [formBin, setFormBin] = useState<string>('');

  // Load Warehouses
  const { data: warehouseData } = useQuery({
    queryKey: ['warehouses', 'list'],
    queryFn: () => trpcClient.warehouses.list.query({ limit: 100 }),
  });
  const warehouses = warehouseData?.items || [];

  // Load Locations
  const { data: locationsData, isLoading } = useQuery({
    queryKey: ['warehouses', 'listLocations', { warehouseId: selectedWarehouseId, search: searchTerm }],
    queryFn: () => trpcClient.warehouses.listLocations.query({
      warehouseId: selectedWarehouseId === 'ALL' ? undefined : selectedWarehouseId,
      search: searchTerm || undefined,
    }),
  });
  const locations = locationsData?.items || [];

  const addMutation = useMutation({
    mutationFn: (data: any) => trpcClient.warehouses.createLocation.mutate(data),
    onSuccess: () => {
      toast.success('Đã thêm vị trí kệ kho mới thành công!');
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'listLocations'] });
      setModalOpen(false);
      resetForm();
    },
    onError: (err: any) => toast.error(`Lỗi thêm kệ: ${err.message}`),
  });

  const updateMutation = useMutation({
    mutationFn: (data: any) => trpcClient.warehouses.updateLocation.mutate(data),
    onSuccess: () => {
      toast.success('Đã cập nhật vị trí kệ kho thành công!');
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'listLocations'] });
      setModalOpen(false);
      resetForm();
    },
    onError: (err: any) => toast.error(`Lỗi cập nhật kệ: ${err.message}`),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => trpcClient.warehouses.deleteLocation.mutate({ id }),
    onSuccess: () => {
      toast.success('Đã xóa vị trí kệ kho thành công!');
      queryClient.invalidateQueries({ queryKey: ['warehouses', 'listLocations'] });
    },
    onError: (err: any) => toast.error(`Lỗi xóa kệ: ${err.message}`),
  });

  const resetForm = () => {
    setEditingLocation(null);
    setFormWarehouseId(warehouses[0]?.id || '');
    setFormCode('');
    setFormName('');
    setFormAisle('');
    setFormShelf('');
    setFormBin('');
  };

  const handleOpenAdd = () => {
    resetForm();
    setFormWarehouseId(selectedWarehouseId !== 'ALL' ? selectedWarehouseId : (warehouses[0]?.id || ''));
    setModalOpen(true);
  };

  const handleOpenEdit = (loc: any) => {
    setEditingLocation(loc);
    setFormWarehouseId(loc.warehouseId);
    setFormCode(loc.code);
    setFormName(loc.name);
    setFormAisle(loc.aisle || '');
    setFormShelf(loc.shelf || '');
    setFormBin(loc.bin || '');
    setModalOpen(true);
  };

  const handleDelete = (loc: any) => {
    if (confirm(`Bạn có chắc chắn muốn XÓA vị trí kệ "${loc.code} - ${loc.name}"?`)) {
      deleteMutation.mutate(loc.id);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formWarehouseId || !formCode || !formName) {
      toast.error('Vui lòng nhập đầy đủ Kho, Mã kệ và Tên kệ');
      return;
    }

    if (editingLocation) {
      updateMutation.mutate({
        id: editingLocation.id,
        warehouseId: formWarehouseId,
        code: formCode,
        name: formName,
        aisle: formAisle,
        shelf: formShelf,
        bin: formBin,
      });
    } else {
      addMutation.mutate({
        warehouseId: formWarehouseId,
        code: formCode,
        name: formName,
        aisle: formAisle,
        shelf: formShelf,
        bin: formBin,
      });
    }
  };

  return (
    <div className="space-y-4 w-full">
      {/* Filter and Add Button Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Select value={selectedWarehouseId} onValueChange={setSelectedWarehouseId}>
            <SelectTrigger className="w-56 h-9 text-xs bg-white">
              <SelectValue placeholder="Chọn kho" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ALL">Tất cả kho</SelectItem>
              {warehouses.map((w: any) => (
                <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="relative w-64">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              placeholder="Tìm theo mã hoặc tên kệ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8 h-9 text-xs"
            />
          </div>
        </div>

        <Button
          onClick={handleOpenAdd}
          className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-xs gap-1.5"
        >
          <Plus className="h-4 w-4" />
          Thêm Vị Trí Kệ Mới
        </Button>
      </div>

      {/* Locations Table */}
      <div className="border rounded-lg bg-white overflow-x-auto shadow-sm">
        <table className="w-full text-xs caption-bottom apv-compact-table">
          <thead className="apv-table-header">
            <tr>
              <th className="h-8 px-3 text-left font-semibold text-slate-700">Mã vị trí kệ</th>
              <th className="h-8 px-3 text-left font-semibold text-slate-700">Tên vị trí</th>
              <th className="h-8 px-3 text-left font-semibold text-slate-700">Thuộc kho</th>
              <th className="h-8 px-3 text-left font-semibold text-slate-700">Dãy (Aisle)</th>
              <th className="h-8 px-3 text-left font-semibold text-slate-700">Tầng (Shelf)</th>
              <th className="h-8 px-3 text-left font-semibold text-slate-700">Ô (Bin)</th>
              <th className="h-8 px-3 text-left font-semibold text-slate-700">Trạng thái</th>
              <th className="h-8 px-3 text-right font-semibold text-slate-700">Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-muted-foreground">Đang tải danh sách vị trí...</td>
              </tr>
            ) : locations.length === 0 ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-muted-foreground italic">
                  Chưa có vị trí kệ nào được khai báo cho kho này.
                </td>
              </tr>
            ) : (
              locations.map((loc: any) => (
                <tr key={loc.id} className="border-b transition-colors hover:bg-slate-50">
                  <td className="p-3 font-mono font-bold text-indigo-700 flex items-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-indigo-500" />
                    {loc.code}
                  </td>
                  <td className="p-3 font-medium text-slate-800">{loc.name}</td>
                  <td className="p-3 text-slate-600">{loc.warehouse?.name || '—'}</td>
                  <td className="p-3 text-slate-600">{loc.aisle || '—'}</td>
                  <td className="p-3 text-slate-600">{loc.shelf || '—'}</td>
                  <td className="p-3 text-slate-600">{loc.bin || '—'}</td>
                  <td className="p-3">
                    <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                      Hoạt động
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-1 relative z-10" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-blue-600 hover:text-blue-800"
                        title="Sửa vị trí kệ"
                        onClick={() => handleOpenEdit(loc)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-rose-600 hover:text-rose-800"
                        title="Xóa vị trí kệ"
                        onClick={() => handleDelete(loc)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add / Edit Location Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>
              {editingLocation ? 'Sửa Vị Trí Kệ Kho' : 'Thêm Vị Trí Kệ Kho Mới'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-3 py-2">
            <div>
              <Label className="text-xs font-semibold">Kho trực thuộc <span className="text-red-500">*</span></Label>
              {/* Ô chọn chung: tìm kiếm + thêm kho mới theo quyền (03/11/2026) */}
              <div className="mt-1">
                <SharedCombobox type="warehouse" value={formWarehouseId} onChange={(id) => setFormWarehouseId(id)} placeholder="Chọn kho" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold">Mã kệ/vị trí <span className="text-red-500">*</span></Label>
                <Input
                  value={formCode}
                  onChange={(e) => setFormCode(e.target.value)}
                  placeholder="VD: KHO-VT-A1"
                  className="h-9 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Tên kệ/vị trí <span className="text-red-500">*</span></Label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="VD: Kệ Giấy Cuộn Dãy A"
                  className="h-9 text-xs mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs font-semibold">Dãy (Aisle)</Label>
                <Input
                  value={formAisle}
                  onChange={(e) => setFormAisle(e.target.value)}
                  placeholder="A"
                  className="h-9 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Tầng (Shelf)</Label>
                <Input
                  value={formShelf}
                  onChange={(e) => setFormShelf(e.target.value)}
                  placeholder="1"
                  className="h-9 text-xs mt-1"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">Ô (Bin)</Label>
                <Input
                  value={formBin}
                  onChange={(e) => setFormBin(e.target.value)}
                  placeholder="01"
                  className="h-9 text-xs mt-1"
                />
              </div>
            </div>

            <DialogFooter className="pt-3">
              <Button type="button" variant="outline" size="sm" onClick={() => setModalOpen(false)}>
                Hủy
              </Button>
              <Button type="submit" size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-white">
                {editingLocation ? 'Lưu Thay Đổi' : 'Tạo Vị Trí Kệ'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
